import { apiClient } from './axios';

export interface WorkflowBatchRun {
  id: string;
  workflow_id: string;
  name: string | null;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'STOPPED';
  delay_ms: number;
  concurrency: number;
  total_rows: number;
  completed_rows: number;
  failed_rows: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  workflow?: { id: string; name: string };
}

export interface WorkflowBatchRunRow {
  id: string;
  batch_run_id: string;
  row_index: number;
  row_data: Record<string, unknown>;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  error_message: string | null;
  execution_log_id: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export const workflowBatchApi = {
  create: async (data: {
    workflow_id: string;
    name?: string;
    delay_ms?: number;
    concurrency?: number;
    data: Record<string, unknown>[];
  }) => {
    const response = await apiClient.post<WorkflowBatchRun>('/workflow-batch-runs', data);
    return response.data;
  },

  upload: async (
    file: File,
    workflowId: string,
    config: { name?: string; delay_ms?: number; concurrency?: number },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workflow_id', workflowId);
    if (config.name) formData.append('name', config.name);
    if (config.delay_ms !== undefined) formData.append('delay_ms', String(config.delay_ms));
    if (config.concurrency !== undefined)
      formData.append('concurrency', String(config.concurrency));
    const response = await apiClient.post<WorkflowBatchRun>(
      '/workflow-batch-runs/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  start: async (id: string) => {
    const response = await apiClient.post<WorkflowBatchRun>(`/workflow-batch-runs/${id}/start`);
    return response.data;
  },

  stop: async (id: string) => {
    const response = await apiClient.post<WorkflowBatchRun>(`/workflow-batch-runs/${id}/stop`);
    return response.data;
  },

  list: async (params: {
    workflow_id?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiClient.get<{
      data: WorkflowBatchRun[];
      total: number;
    }>('/workflow-batch-runs', { params });
    return response.data;
  },

  get: async (id: string) => {
    const response = await apiClient.get<WorkflowBatchRun>(`/workflow-batch-runs/${id}`);
    return response.data;
  },

  getRows: async (id: string, params: { page?: number; limit?: number; status?: string }) => {
    const response = await apiClient.get<{
      data: WorkflowBatchRunRow[];
      total: number;
    }>(`/workflow-batch-runs/${id}/rows`, { params });
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/workflow-batch-runs/${id}`);
  },
};
